import { useEffect, useMemo, useState } from "react";
import {
    getActiveModule,
    getGridType,
    isDisabledUI,
    toggleStatusBtn,
} from "@ts/utils";
import { showToast } from "@ts/modals";
import {
    MODULE_MAPPING,
    SKU_DEPENDENT_MODULES,
    STATE_SUBSCRIBE_CONFIG,
} from "@ts/constants";
import * as Sentry from "@sentry/react";
import $ from "jquery";
import { useCampaignSyncCheck, useGetModulesData, usePermissionCheck } from "@hooks/AllCustomHooks";
import { setUpdateGridData } from "@ts/filters";
import {
    CAMPAIGN_SYNC_STATE,
    Channels,
    DeepDiveGridType,
    IChannel,
    ICustomCellRendererParams,
    Modules,
    ParentModule,
    RecordType,
    SubModule,
    TSubscribeAction,
    TSubscribeActionResponse,
} from "@ts/common-types";
import { useDispatch, useSelector } from "react-redux";
import {
    updateDailyBudgetModalData,
    updateDailyBudgetReviewModalDisplay,
    updateCampaignConfigModalDisplay,
    updateIsCampaignStateAction,
} from "@features/modalSlice";
import store from "@/store";
import { includes } from "underscore";
import { Button, Popover } from "antd";
/**
 * !Note[DND]:
 * ? Just, in case any refactoring is done related to it, then please, test the deepdive aggrid dashboard components also
 * * [1] `isDeepDiveEnabled` logic is required cause, this cell renderer is used in both deep dive and campaigns
 * * [2] When being used in deep-dive, we use it in modals or create a new module with different route
 * * [3] In both of the cases the we might not write the grid related permission and definitions corresponding to module which already exists
 * * [4] For eg, Campaigns already exists to we can reuse it, in a different module, but to use it the the activeModule should be `campaings`
 * * [5] Since, moduleConfig is dependent on activeModule and activeParentModule, we will get error and to fix this we have again copy the grid related data
 * * [6] In the moduleConfig against the module name, which is not feasible to copy
 * * [7] To prevent such duplication and reuse the existing module, we have written this condition
 */

const StatusCellRenderer = (
    params: ICustomCellRendererParams,
    contextType: string
) => {
    const dispatch = useDispatch();
    const [isUIDisabled, setIsUIDisabled] = useState<boolean>(false);
    const [inputDisp, setInputDisp] = useState<string>("block");
    const [inputGroupEl, setInputGroupEl] = useState<string>(
        "form-check form-switch"
    );
    const [spinnerDisp, setSpinnerDisp] = useState<string>("none");
    const [inputId, setInputId] = useState<string>("");
    const [inputAttr, setInputAttr] = useState<string | number>("");
    const [inputChecked, setInputChecked] = useState<boolean>(false);
    const [paramsDataId, setParamsDataId] = useState<string | number>("");
    const { activeModule } = getActiveModule();
    const isDeepDiveEnabled = useMemo(
        () => includes(SKU_DEPENDENT_MODULES, activeModule),
        [activeModule]
    );

    const [popoverVisible, setPopoverVisible] = useState(false);
    const { isCampaignSyncCheckRequired } = useCampaignSyncCheck({params});

    // Refer the note at top for isDeepDiveEnabled usecase
    const moduleParams = useMemo(() => {
        if (!isDeepDiveEnabled) return {};

        const moduleMap: Partial<
            Record<
                DeepDiveGridType,
                {
                    activeModuleName: Modules;
                    activeParentModuleName: ParentModule;
                }
            >
        > = {
            [DeepDiveGridType.Campaign]: {
                activeModuleName: SubModule.CAMPAIGN,
                activeParentModuleName: ParentModule.PERFORMANCE_OVERVIEW,
            },
            [DeepDiveGridType.Keyword]: {
                activeModuleName: SubModule.KEYWORD,
                activeParentModuleName: ParentModule.PRODUCT_ANALYTICS,
            },
        };

        return moduleMap[contextType as DeepDiveGridType] || {};
    }, [contextType, isDeepDiveEnabled]);

    const actions = useGetModulesData({
        type: "actions",
        ...moduleParams,
    });

    const channel = useSelector((state: IChannel) => state.channel.value);

    // Refer the note at top for isDeepDiveEnabled usecase
    const gridParams = useGetModulesData({
        type: "grid-params",
        ...(isDeepDiveEnabled ? moduleParams : { subGridType: getGridType() }),
    });

    const { isModuleEditDisabled } = usePermissionCheck({
        type: "cellRenderer",
    });

    const isKeywordPauseAllowed =
        gridParams && Object.keys(gridParams).includes("isKeywordPauseAllowed")
            ? gridParams.isKeywordPauseAllowed
            : true;

    const pauseOrArchiveState = isKeywordPauseAllowed ? "paused" : "archived";

    const isCampaignRestartAllowed =
        gridParams?.isCampaignRestartAllowed || false;

    const waitForUpdateResponse = (action: TSubscribeAction) => {
        const { key, message } = STATE_SUBSCRIBE_CONFIG[action];
        return new Promise<string>((resolve) => {
            const unsubscribe = store.subscribe(() => {
                const subscribeActionResponse = (
                    store.getState() as unknown as TSubscribeActionResponse
                )[key].value;

                if (Object.keys(subscribeActionResponse).length) {
                    resolve(message);
                    unsubscribe();
                }
            });
        });
    };

    const toggleSwitchHandler = async () => {
        if (contextType === "automate_bid") {
            const isStatusButtonChecked = $(
                `#${contextType}-status-btn-${+params.node.rowIndex + 1}`
            ).prop("checked");
            const campaignName = params.node.data.name
                .split(/[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/)
                .filter((e: string) => e)
                .join("-");
            const placementTopElementID = `placementTop-${campaignName}__${params.node.data.campaignType}__${params.node.data.targetingType}__${params.node.data.targetingClass}-update-field`;

            const placementProductPageElementID = `placementProductPage-${campaignName}__${params.node.data.campaignType}__${params.node.data.targetingType}__${params.node.data.targetingClass}-update-field`;

            const placementTopElement = document.getElementById(
                placementTopElementID
            );

            const placementProductPageElement = document.getElementById(
                placementProductPageElementID
            );

            placementTopElement.toggleAttribute("disabled");
            placementProductPageElement.toggleAttribute("disabled");

            params.data.isAutomateBidChecked = isStatusButtonChecked;
        }

        if (contextType === "campaign") {
            const isChecked = $(
                `#${contextType}-status-btn-${paramsDataId}`
            ).prop("checked");

            // Daily budget config update logic
            if (
                channel === Channels.FLIPKART &&
                params.data.daily_budget &&
                isChecked
            ) {
                dispatch(updateDailyBudgetModalData(params.data));
                dispatch(updateDailyBudgetReviewModalDisplay(true));
                await waitForUpdateResponse("dailyBudget");
            }

            //Campaign Restart/Stop logic
            if (isCampaignRestartAllowed) {
                if (isChecked) {
                    dispatch(updateIsCampaignStateAction(true));
                    dispatch(updateDailyBudgetModalData(params.data));
                    dispatch(updateCampaignConfigModalDisplay(true));
                    await waitForUpdateResponse("state");
                    return;
                } else {
                    const shouldStopCampaign = window.confirm(
                        "Are you sure you want to stop this campaign?"
                    );
                    if (!shouldStopCampaign) {
                        return;
                    }
                }
            }

            await toggleStatus(paramsDataId, contextType, isChecked);
        } else {
            await toggleStatus(paramsDataId, contextType);
        }
    };

    useEffect(() => {
        moduleSwitchCase(contextType);
        // eslint-disable-next-line
    }, [contextType, actions, isModuleEditDisabled, isCampaignSyncCheckRequired]);

    const moduleSwitchCase = (context: string) => {
        switch (context) {
            case "portfolio":
                setIsUIDisabled(!params.data.portfolio_id);
                setInputId(`${context}-status-btn-${params.data.portfolio_id}`);
                setInputAttr(params.data.portfolio_id);
                setInputChecked(params.data.portfolio_state === "enabled");
                setParamsDataId(params.data.portfolio_id);
                break;
            case "keyword":
                setInputChecked(params.data.state === "enabled");
                setInputId(
                    `${context}-status-btn-${params.data.sys_ams_keyword_id}`
                );
                setInputAttr(params.data.sys_ams_keyword_id);
                setParamsDataId(params.data.sys_ams_keyword_id);
                setIsUIDisabled(
                    params.data.keyword_id === "00000000000000" ||
                        !params.data.sys_ams_keyword_id ||
                        params.data.is_archived_entity ||
                        isDisabledUI([params.data.serving_status]) ||
                        // !NOTE: This is temporary, had to be removed post dicsussion
                        (channel === Channels.TALABAT &&
                            (params.data.record_type as string) ===
                                RecordType.TARGET)
                );
                break;
            case "ad-group":
                setIsUIDisabled(
                    !params.data.sys_adgroup_id ||
                        !params.data.state ||
                        gridParams.disabledProgramTypes?.includes(
                            params.data.program_type
                        ) ||
                        isDisabledUI([
                            params.data.adgroup_status,
                            params.data.state,
                        ])
                );
                setInputId(
                    `${context}-status-btn-${params.data.sys_adgroup_id}`
                );
                setInputAttr(params.data.sys_adgroup_id);
                setInputChecked(params.data.state === "enabled");
                setParamsDataId(params.data.sys_adgroup_id);
                break;
            case "campaign":
                let disabledState = !(
                    isCampaignRestartAllowed &&
                    params.data?.allowed_actions?.includes("RESTART")
                ) &&
                    (!params.data.sys_campaign_id ||
                        !params.data.state ||
                        isDisabledUI([params.data.campaign_status]));

                if (isCampaignSyncCheckRequired) {
                    const isPending = params.data.sync_state === CAMPAIGN_SYNC_STATE.PENDING;
                    disabledState = isPending || !!disabledState;
                }

                setIsUIDisabled(disabledState);
                setInputId(
                    `${context}-status-btn-${params.data.sys_campaign_id}`
                );
                setInputAttr(params.data.sys_campaign_id);
                setInputChecked(params.data.state === "enabled");
                setParamsDataId(params.data.sys_campaign_id);
                break;
            case "productad":
                setIsUIDisabled(!params.data.ad_id);
                setInputId(`${context}-status-btn-${params.data.ad_id}`);
                setInputAttr(params.data.ad_id);
                setInputChecked(params.data.state === "enabled");
                setParamsDataId(params.data.ad_id);
                break;
            case "automate_bid":
                if (params.data.campaignType === "sponsoredProducts") {
                    setIsUIDisabled(true);
                    setInputChecked(params.data.isAutomateBidChecked);
                } else {
                    const rowId = +params.node.rowIndex + 1;
                    setIsUIDisabled(!rowId);
                    setParamsDataId(rowId);
                    setInputAttr(rowId);
                    setInputChecked(params.data.isAutomateBidChecked);
                    setInputId(`${context}-status-btn-${rowId}`);
                }
                break;
            case "products":
                setIsUIDisabled(!params.data.ad_id);
                setInputId(`${context}-status-btn-${params.data.ad_id}`);
                setInputAttr(params.data.ad_id);
                setInputChecked(params.data.state === "enabled");
                setParamsDataId(params.data.ad_id);
                break;
            default:
                console.info(
                    `Module ${context} is not available for the intended action`
                );
        }
    };

    async function toggleStatus(
        id: number | string,
        context: string,
        isChecked = false
    ): Promise<void> {
        setInputDisp("none");
        setSpinnerDisp("block");
        setInputGroupEl("");

        try {
            const isStatusButtonChecked = isChecked
                ? isChecked
                : $(`#${context}-status-btn-${id}`).prop("checked");

            // Setting campaign state to archived if stop campaign action is initiated
            const newState = isCampaignRestartAllowed
                ? "archived"
                : isStatusButtonChecked
                ? "enabled"
                : pauseOrArchiveState;
            const previousState = !isStatusButtonChecked ? "enabled" : "paused";
            const isDprActive = params.data.automation
                ? params.data.automation
                : false;

            const result = await toggleStatusBtn(
                id,
                context,
                newState,
                isDprActive
            );
            if (!result.status) {
                throw new Error(result.message);
            }
            if (context !== "automate_bid") {
                showToast(
                    "success",
                    `Status of ${context}: ${
                        params.data[MODULE_MAPPING[context].nameField] ||
                        params.data.keyword_name ||
                        params.data.keyword ||
                        params.data.name
                    } has been changed from ${previousState} to ${newState}
						`
                );
            }
            /**
             * ! Added recently have to be tested
             */
            setInputChecked(!inputChecked);
            !params?.isModalComponent &&
                setUpdateGridData({
                    params,
                    value: newState,
                    isReduxState: true,
                    additionalValuesToUpdate: {
                        ...(!isKeywordPauseAllowed && {
                            serving_status: pauseOrArchiveState,
                        }),
                        ...(isCampaignRestartAllowed && {
                            campaign_status: "stopped",
                            is_archived_entity: true,
                        }),
                    },
                });
        } catch (err) {
            console.error(err);
            showToast(
                "danger",
                `Error while updating status for ${context}: ${
                    params.data[MODULE_MAPPING[context].nameField]
                } : ${(err as Error).message}`
            );

            Sentry.captureException(err);
        }

        setInputGroupEl("form-check form-switch");
        setSpinnerDisp("none");
        setInputDisp("block");
    }

    const handleYes = () => {
        const statusToggleElement = $(
            `#${contextType}-status-btn-${paramsDataId}`
        );
        statusToggleElement.prop(
            "checked",
            !statusToggleElement.prop("checked")
        );
        toggleSwitchHandler();
        setPopoverVisible(false);
    };

    const CheckboxElement = (
        <input
            type="checkbox"
            name="status-toggle"
            id={inputId}
            data-id={inputAttr}
            checked={inputChecked}
            className={`${contextType}-status-btn form-check-input`}
            style={{ display: inputDisp }}
            onChange={isKeywordPauseAllowed ? toggleSwitchHandler : undefined}
            disabled={isUIDisabled}
        />
    );

    const PopoverContent = (
        <div>
            <p>
                Status pause is not allowed and keyword will be removed instead,
                Do you want to proceed?
            </p>
            <div style={{ display: "flex", justifyContent: "end" }}>
                <Button type="primary" size="small" onClick={handleYes}>
                    Yes
                </Button>
                <Button
                    size="small"
                    className="ms-2"
                    onClick={() => {
                        setPopoverVisible(false);
                    }}
                >
                    No
                </Button>
            </div>
        </div>
    );

    return (
        <>
            {params.node.rowPinned ? null : (
                <div
                    className="ag-cell-wrapper"
                    style={{ display: "grid", placeContent: "center" }}
                    data-status={
                        isUIDisabled
                            ? "archived"
                            : inputChecked
                            ? "enabled"
                            : "paused"
                    }
                >
                    <div className={inputGroupEl}>
                        {!isKeywordPauseAllowed ? (
                            <Popover
                                className="mx-2"
                                styles={{
                                    body: {
                                        padding: "15px",
                                        maxWidth: "250px",
                                    },
                                }}
                                placement="right"
                                content={PopoverContent}
                                trigger="click"
                                open={popoverVisible}
                                onOpenChange={setPopoverVisible}
                            >
                                {CheckboxElement}
                            </Popover>
                        ) : (
                            CheckboxElement
                        )}
                        <button
                            className="spinner-border text-primary"
                            style={{ display: `${spinnerDisp}` }}
                            disabled={true}
                        ></button>
                    </div>
                </div>
            )}
        </>
    );
};

export default StatusCellRenderer;
